import { Button, ConfirmationDialog, TextInput, cn, useTranslate } from '@athena/ui'
import { useBlocker } from '@tanstack/react-router'

/**
 * Leaving a form with edits that were not saved.
 *
 * Athena's own dialog, not the browser's `window.confirm`: it says what is
 * lost in the product's words and language, and "Keep editing" is the
 * recommended, first-focused way back. `caution`, because what goes is typing
 * that has not been saved, not anything the server holds.
 *
 * Closing the tab or reloading still gets the browser's own prompt: a page
 * that is unloading cannot draw one of its own, and the browser allows
 * nothing else there.
 */
export function UnsavedChanges({ dirty }: { dirty: boolean }) {
  const t = useTranslate()
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: dirty,
    withResolver: true,
  })
  const blocked = blocker.status === 'blocked'

  return (
    <ConfirmationDialog
      open={blocked}
      onOpenChange={(open) => {
        // Leaving is decided first when it is chosen; staying a tick later,
        // so that a navigation told to go is not then told to stay.
        const reset = blocked ? blocker.reset : undefined
        if (!open) queueMicrotask(() => reset?.())
      }}
      intent="caution"
      title={t('management.unsaved')}
      description={t('management.unsavedBody')}
      confirmLabel={t('management.leaveWithoutSaving')}
      cancelLabel={t('management.keepEditing')}
      onConfirm={() => {
        if (blocked) blocker.proceed()
      }}
    />
  )
}

/**
 * The reason every privileged change asks for, the same everywhere: one
 * label, one line of help, required, and a single line — a reason is a
 * sentence for the audit log, not a letter. Every screen and every dialog
 * uses this one, so the field never changes shape between two actions.
 *
 * `required` is announced but not enforced by the browser: the forms around
 * it are `noValidate`, and Athena says what is missing in its own words
 * rather than in a browser tooltip beside its own message.
 */
export function ReasonField({
  value,
  onChange,
  error = '',
  className,
}: {
  value: string
  onChange: (value: string) => void
  error?: string
  className?: string
}) {
  const t = useTranslate()
  return (
    <div className={cn('w-full max-w-xl', className)}>
      <TextInput
        label={t('management.reason')}
        helperText={t('management.reasonHelper')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
        maxLength={1000}
        error={error}
      />
    </div>
  )
}
/**
 * Previous, Next, and where in the list this page is. The page lives in the
 * URL, so these are links a browser's Back undoes, not state a reload loses.
 * A landmark of its own, named, so it can be found without reading the table.
 */
export function PageControls({
  page,
  total,
  limit,
  onChange,
  disabled,
}: {
  /** One-based. */
  page: number
  total: number
  limit: number
  onChange: (page: number) => void
  disabled: boolean
}) {
  const t = useTranslate()
  const first = total === 0 ? 0 : (page - 1) * limit + 1
  const last = Math.min(page * limit, total)
  if (total <= limit && page === 1) return null
  return (
    <nav
      aria-label={t('admin.pagination')}
      className="flex flex-wrap items-center justify-between gap-3"
    >
      <span aria-live="polite" className="text-body-sm text-text-secondary">
        {t('management.page', [String(first), String(last), String(total)])}
      </span>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="max-md:h-touch-target-min"
          disabled={disabled || page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t('management.previous')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="max-md:h-touch-target-min"
          disabled={disabled || last >= total}
          onClick={() => onChange(page + 1)}
        >
          {t('management.next')}
        </Button>
      </div>
    </nav>
  )
}

/**
 * A list with nothing to show, said in one line where the table would be.
 *
 * Compact on purpose: an administrator on an empty list needs to know which
 * kind of empty it is — nothing exists, nothing matches, nothing is waiting —
 * and, when a filter is the reason, a way out of it. Not an illustration.
 */
export function ListMessage({
  children,
  action,
}: {
  children: string
  action?: { label: string; onClick: () => void } | undefined
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-border-strong px-4 py-4">
      <p className="text-body-sm text-text-secondary">{children}</p>
      {action ? (
        <Button type="button" variant="ghost" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}
